<template>
  <div class="room-container">
    <!-- 替换原有的 el-row 布局为可拖拽布局 -->
    <div class="resizable-layout margin-top-sm">
      <!-- 左侧树形组件 -->
      <div class="tree-panel" :style="{ width: treeWidth + 'px' }">
        <floor-unit-tree ref="floorUnitTree" @switchFloorUnit="switchFloorUnit" />
      </div>

      <!-- 拖拽分隔线 -->
      <div class="resize-handle" @mousedown="startResize" @mouseover="showResizeCursor" @mouseleave="hideResizeCursor">
        <div class="resize-line"></div>
      </div>

      <!-- 右侧内容区域 -->
      <div class="content-panel" :style="{ width: 'calc(100% - ' + (treeWidth + 10) + 'px)' }">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('roomList.page.title')" :subtitle="$t('roomList.page.subtitle')">
          <template #extra>
            <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddRoomFloorModal">
              {{ $t('roomList.addBuilding') }}
            </el-button>
            <el-button size="mini" icon="el-icon-edit" :disabled="!roomInfo.conditions.floorId"
              @click="openUpdateRoomFloorModal">
              {{ $t('roomList.editBuilding') }}
            </el-button>
            <el-button size="mini" icon="el-icon-delete" :disabled="!roomInfo.conditions.floorId"
              @click="openDeleteRoomFloorModal">
              {{ $t('roomList.deleteBuilding') }}
            </el-button>
            <el-button size="mini" icon="el-icon-plus" :disabled="!roomInfo.conditions.floorId"
              @click="openAddRoomUnitModal">
              {{ $t('roomList.addUnit') }}
            </el-button>
            <el-button size="mini" icon="el-icon-edit" :disabled="!roomInfo.conditions.unitId"
              @click="openUpdateRoomUnitModal">
              {{ $t('roomList.editUnit') }}
            </el-button>
            <el-button size="mini" icon="el-icon-delete" :disabled="!roomInfo.conditions.unitId"
              @click="openDeleteRoomUnitModal">
              {{ $t('roomList.deleteUnit') }}
            </el-button>
            <el-button v-if="hasPrivilege('502020082453087940')" type="primary" size="mini" icon="el-icon-plus"
              @click="openAddRoom">
              {{ $t('roomList.addRoom') }}
            </el-button>
            <el-button size="mini" icon="el-icon-upload2" @click="openImportRoomInfo">
              {{ $t('roomList.importRoom') }}
            </el-button>
          </template>
        </ds-page-header>

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="roomInfo.conditions.roomId" clearable class="w-full"
                :placeholder="$t('roomList.roomIdPlaceholder')" @keyup.enter.native="queryRoomMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="roomInfo.conditions.state" clearable class="w-full"
                :placeholder="$t('roomList.selectState')">
                <el-option v-for="(item, index) in roomInfo.states" :key="index" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="roomInfo.conditions.roomSubType" clearable class="w-full"
                :placeholder="$t('roomList.selectRoomType')">
                <el-option v-for="(item, index) in roomInfo.roomSubTypes" :key="index" :label="item.name"
                  :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="roomInfo.isAll" class="w-full" @change="changeIsAll">
                <el-option :label="$t('roomList.currentFloorUnit')" value="N" />
                <el-option :label="$t('roomList.allFloorUnit')" value="Y" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryRoomMethod">
              {{ $t('roomList.query') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetRoomInfo">
              {{ $t('roomList.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="roomInfo.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="listRoom(roomInfo.currentPage, roomInfo.size)">
            <template #left>
              <span class="ds-panel__title">{{ $t('roomList.roomListTitle') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="roomInfo.rooms" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('roomList.page.empty')"
            @retry="listRoom(roomInfo.currentPage, roomInfo.size)">
            <el-table-column prop="roomId" :label="$t('roomList.room')" width="120" fixed="left" align="center"
              class-name="text-center hc-table-fix-left-td">
              <template slot-scope="scope">
                <span class="ds-num">
                  {{ scope.row.floorNum }}-{{ scope.row.unitNum }}-{{ scope.row.roomNum }}
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="layer" :label="$t('roomList.floor')" width="90" align="center">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.layer }}{{ $t('roomList.layer') }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="ownerName" :label="$t('roomList.owner')" width="150" align="center">
              <template slot-scope="scope">
                <a v-if="scope.row.ownerName" target="_blank"
                  :href="'/#/views/owner/ownerDetail?ownerId=' + scope.row.ownerId">
                  {{ scope.row.ownerName }}({{ scope.row.link }})
                </a>
                <span v-else class="ds-cell-sub">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="roomSubTypeName" :label="$t('roomList.type')" width="110" align="center" />
            <!-- 面积：建筑面积 / 套内面积 两行主次分明 -->
            <el-table-column :label="$t('roomList.area')" width="140" align="center">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.builtUpArea }}{{ $t('roomList.squareMeter') }}</div>
                <div class="ds-num ds-cell-sub">{{ scope.row.roomArea }}{{ $t('roomList.squareMeter') }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="roomRent" :label="$t('roomList.rent')" width="100" align="right">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.roomRent != null ? scope.row.roomRent : '-' }}</span>
              </template>
            </el-table-column>
            <!-- 状态 Tag：颜色仅作辅助，始终带文字（WCAG 1.4.1） -->
            <el-table-column prop="stateName" :label="$t('roomList.roomStatus')" width="120" align="center">
              <template slot-scope="scope">
                <ds-status-tag :type="resolveState(scope.row)" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <!-- 有效期：起止两行 -->
            <el-table-column :label="$t('roomList.validity')" width="150" align="center">
              <template slot-scope="scope">
                <div class="ds-num">{{ scope.row.startTime || '-' }}</div>
                <div class="ds-num ds-cell-sub">{{ scope.row.endTime || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerMembers')" width="110" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOwnerMembers(scope.row)">
                  {{ scope.row.memberCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerCars')" width="110" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOwnerCars(scope.row)">
                  {{ scope.row.carCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerRooms')" width="110" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOwnerRooms(scope.row)">
                  {{ scope.row.roomCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.complaints')" width="100" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewComplaints(scope.row)">
                  {{ scope.row.complaintCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.repairs')" width="100" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewRepairs(scope.row)">
                  {{ scope.row.repairCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <!-- 欠费金额：右对齐等宽数字 -->
            <el-table-column :label="$t('roomList.roomOweFee')" width="120" align="right">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewRoomOweFees(scope.row)">
                  {{ formatMoney(scope.row.roomOweFee) }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.ownerOweFee')" width="120" align="right">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewOweFees(scope.row)">
                  {{ formatMoney(scope.row.oweFee) }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.roomContracts')" width="120" align="center">
              <template slot-scope="scope">
                <a href="javascript:void(0)" @click="viewRoomContracts(scope.row)">
                  {{ scope.row.contractCount || 0 }}
                </a>
              </template>
            </el-table-column>
            <el-table-column v-for="(item, index) in roomInfo.listColumns" :key="index" :label="item" width="120"
              align="center">
              <template slot-scope="scope">
                <div v-if="scope.row.listValues">{{ scope.row.listValues[index] || '' }}</div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('roomList.operation')" fixed="right" width="200" align="center">
              <template slot-scope="scope">
                <el-button v-if="hasPrivilege('502020082493857941')" size="mini" type="text"
                  @click="openEditRoomModel(scope.row)">
                  {{ $t('roomList.edit') }}
                </el-button>
                <el-button v-if="hasPrivilege('502020082417457942')" size="mini" type="text" class="ds-btn-danger"
                  @click="openDelRoomModel(scope.row)">
                  {{ $t('roomList.delete') }}
                </el-button>
                <el-button v-if="scope.row.state == '2002' && hasPrivilege('502023021978930012')" size="mini" type="text"
                  @click="toRoomBindOwner(scope.row)">
                  {{ $t('roomList.handOver') }}
                </el-button>
                <el-button v-if="scope.row.state != '2002' && scope.row.ownerId && hasPrivilege('502023021973760015')"
                  size="mini" type="text" @click="toRoomUnBindOwner(scope.row)">
                  {{ $t('roomList.checkOut') }}
                </el-button>
                <el-button v-if="scope.row.state != '2002'" size="mini" type="text" @click="toSimplifyAcceptance(scope.row)">
                  {{ $t('roomList.businessAcceptance') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="roomInfo.total" :page="roomInfo.currentPage" :size="roomInfo.size"
            :page-sizes="[10, 20, 30, 40]" layout="total, sizes, prev, pager, next, jumper" @size-change="handleSizeChange"
            @current-change="handleCurrentChange">
            <template #summary>
              <div class="ds-tip-text">
                <div>{{ $t('roomList.roomStatusDesc1') }}</div>
                <div>{{ $t('roomList.roomStatusDesc2') }}</div>
                <div>{{ $t('roomList.roomStatusDesc3') }}</div>
                <div>{{ $t('roomList.rentDesc') }}</div>
              </div>
            </template>
          </ds-pagination-bar>
        </div>
      </div>
    </div>

    <!-- 子组件 -->
    <add-floor ref="addFloor" @handleRefreshTree="floorUnitTree" />
    <edit-floor ref="editFloor" @handleRefreshTree="floorUnitTree" />
    <delete-floor ref="deleteFloor" @handleRefreshTree="floorUnitTree" />
    <add-unit ref="addUnit" @handleRefreshTree="floorUnitTree" />
    <edit-unit ref="editUnit" @handleRefreshTree="floorUnitTree" />
    <delete-unit ref="deleteUnit" @handleRefreshTree="floorUnitTree" />
    <search-floor ref="searchFloor" emit-choose-floor="room" emit-load-data="room" />
    <edit-room ref="editRoom" @handleRefreshRoom="handleRefreshRoom" />
    <delete-room ref="deleteRoom" @handleRefreshRoom="handleRefreshRoom" />
    <import-owner-room ref="importOwnerRoom" />

    <owner-rooms ref="ownerRooms" />
    <owner-members ref="ownerMembers" />
    <owner-cars ref="ownerCars" />
    <owner-complaints ref="ownerComplaints" />
    <owner-repairs ref="ownerRepairs" />
    <owner-owe-fees ref="ownerOweFees" />
    <room-owe-fees ref="roomOweFees" />
    <room-contracts ref="roomContracts" />
  </div>
</template>

<script>
import { getDict } from '@/api/community/communityApi'
import { queryRooms, getFloors, getUnits } from '@/api/room/roomApi'
import FloorUnitTree from '@/components/room/floorUnitTree'
import SearchFloor from '@/components/room/searchFloor'
import EditRoom from '@/components/room/editRoom'
import DeleteRoom from '@/components/room/deleteRoom'
import ImportOwnerRoom from '@/components/room/importOwnerRoom'
import AddFloor from '@/components/room/addFloor'
import EditFloor from '@/components/room/editFloor'
import DeleteFloor from '@/components/room/deleteFloor'
import AddUnit from '@/components/room/addUnit'
import EditUnit from '@/components/room/editUnit'
import DeleteUnit from '@/components/room/deleteUnit'
import OwnerRooms from '@/components/owner/ownerRooms'
import OwnerMembers from '@/components/owner/ownerMembers'
import OwnerCars from '@/components/owner/ownerCars'
import OwnerComplaints from '@/components/owner/ownerComplaints'
import OwnerRepairs from '@/components/owner/ownerRepairs'
import OwnerOweFees from '@/components/owner/ownerOweFees'
import RoomOweFees from '@/components/room/roomOweFees'
import RoomContracts from '@/components/room/roomContracts'

import { getAttrSpecList } from '@/api/dev/attrSpecApi'
// import { getAttrValueList } from '@/api/dev/attrValueApi'

import { resolveStatus } from '@/utils/statusMap'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

const DEFAULT_PAGE = 1
const DEFAULT_ROW = 10

export default {
  name: 'RoomList',
  components: {
    FloorUnitTree,
    SearchFloor,
    EditRoom,
    DeleteRoom,
    ImportOwnerRoom,
    AddFloor,
    EditFloor,
    DeleteFloor,
    AddUnit,
    EditUnit,
    DeleteUnit,
    OwnerRooms,
    OwnerMembers,
    OwnerCars,
    OwnerComplaints,
    OwnerRepairs,
    OwnerOweFees,
    RoomOweFees,
    RoomContracts,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      DEFAULT_PAGE,
      DEFAULT_ROW,
      callbackListener: 'room',
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      roomInfo: {
        rooms: [],
        total: 0,
        records: 1,
        floorId: '',
        unitId: '',
        states: [],
        roomNum: '',
        isAll: 'N',
        moreCondition: false,
        conditions: {
          floorId: '',
          floorName: '',
          unitId: '',
          roomNum: '',
          roomId: '',
          state: '',
          section: '',
          roomType: '1010301',
          roomSubType: '',
          flag: ''
        },
        currentPage: DEFAULT_PAGE,
        size: DEFAULT_ROW,
        listColumns: [],
        roomSubTypes: []
      },
      // 新增：拖拽相关数据
      treeWidth: 300, // 默认树形组件宽度
      minTreeWidth: 200, // 最小宽度
      maxTreeWidth: 600, // 最大宽度
      isResizing: false,
      startX: 0,
      startWidth: 0
    }
  },
  mounted() {
    this.initData()
    // 恢复保存的宽度
    this.restoreTreeWidth()
    // 添加全局鼠标事件监听
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
  },
  beforeDestroy() {
    // 移除全局事件监听
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
  },
  methods: {
    /**
     * 金额格式化（只改展示，不改原值）
     */
    formatMoney(value) {
      return formatMoney(value)
    },
    /**
     * 状态语义类型（房屋）：颜色仅作辅助，始终带文字。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'room',
        text: row.stateName || ''
      }).type
    },
    async initData() {
      this.roomInfo.conditions.floorId = this.$route.query.floorId || ''
      this.roomInfo.conditions.floorName = this.$route.query.floorName || ''

      try {
        // 获取房屋状态字典
        const stateData = await getDict('building_room', 'state')
        this.roomInfo.states = Array.isArray(stateData) ? stateData : []

        // 获取房屋类型字典
        const roomTypeData = await getDict('building_room', 'room_sub_type')
        this.roomInfo.roomSubTypes = Array.isArray(roomTypeData) ? roomTypeData : []

        this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
      } catch (error) {
        console.error('初始化数据失败:', error)
      }
    },
    handleRefreshRoom() {
      this.listRoom(this.roomInfo.currentPage, this.roomInfo.size)
    },
    async listRoom(page, row) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      this.roomInfo.currentPage = page
      this.roomInfo.size = row
      this.roomInfo.conditions.page = page
      this.roomInfo.conditions.row = row
      this.roomInfo.conditions.communityId = this.getCommunityId()

      const params = { ...this.roomInfo.conditions }

      // 处理房间号格式
      const allNum = params.roomId || ''
      const allNums = allNum.split('-')
      if (allNums.length >= 3) {
        params.floorNum = allNums[0].trim()
        params.unitNum = allNums[1].trim()
        params.roomNum = allNums.slice(2).join('-').trim()
        params.roomId = ''
        params.unitId = ''
        params.floorId = ''
      }

      try {
        const res = await queryRooms(params)
        this.roomInfo.total = res.total || 0
        this.roomInfo.records = res.records
        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 rooms 字段的情况。
        this.roomInfo.rooms = Array.isArray(res.rooms) ? res.rooms : []
        await this.dealRoomAttr(this.roomInfo.rooms)
      } catch (error) {
        console.error('获取房间列表失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('roomList.page.loadFailed')
        this.$message.error(this.$t('roomList.page.loadFailed'))
      } finally {
        this.loading = false
      }
    },

    async dealRoomAttr(rooms) {
      if (!rooms || !rooms.length) return

      // 获取表格列配置后再处理每个房屋的属性值
      await this.getColumns()
      rooms.forEach(item => {
        this.getColumnsValue(item)
      })
    },

    getColumnsValue(room) {
      room.listValues = []
      if (!room.roomAttrDto || !room.roomAttrDto.length) {
        this.roomInfo.listColumns.forEach(() => {
          room.listValues.push('')
        })
        return
      }

      this.roomInfo.listColumns.forEach(column => {
        const attr = room.roomAttrDto.find(dto => dto.specName === column)
        room.listValues.push(attr ? attr.valueName : '')
      })
    },

    async getColumns() {
      try {
        const { data } = await getAttrSpecList({
          page: 1,
          row: 100,
          tableName: 'building_room_attr'
        })
        this.roomInfo.listColumns = data
          .filter(item => item.listShow === 'Y')
          .map(item => item.specName)
      } catch (error) {
        console.error('获取房屋属性失败:', error)
      }
    },

    switchFloorUnit(data) {
      this.roomInfo.conditions.floorId = data.floorId
      this.roomInfo.conditions.unitId = data.unitId
      this.roomInfo.conditions.flag = 1
      this.roomInfo.isAll = 'N'
      this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
    },

    handleSizeChange(val) {
      // 与原逻辑一致：仅改变每页条数，停留在当前页
      this.listRoom(this.roomInfo.currentPage, val)
    },

    handleCurrentChange(val) {
      // 与原逻辑一致：翻页时每页条数回到默认值
      this.listRoom(val, DEFAULT_ROW)
    },


    handleResize() {
      this.$forceUpdate()
    },

    floorUnitTree(param) {
      this.$refs.floorUnitTree.refreshTree(param)
    },
    // 按钮操作相关方法
   async openAddRoom() {
      let _floorId = this.roomInfo.conditions.floorId;
      if(this.roomInfo.conditions.unitId){
        const unitData = await this.loadUnits(this.roomInfo.conditions.unitId)
        if(unitData && unitData.floorId){
          _floorId = unitData.floorId;
        }
      }
      if(!_floorId){
        this.$message.warning('请先选择楼栋')
        return
      }
      this.$router.push('/views/room/addRoomView?floorId='+_floorId+"&unitId="+this.roomInfo.conditions.unitId)
    },

    openEditRoomModel(room) {
      this.$refs.editRoom.open(room)
    },

    openDelRoomModel(room) {
      this.$refs.deleteRoom.open(room)
    },

    queryRoomMethod() {
      this.roomInfo.conditions.flag = 0
      this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
    },

    resetRoomInfo() {
      this.roomInfo.conditions = {
        floorId: '',
        floorName: '',
        unitId: '',
        roomNum: '',
        roomId: '',
        state: '',
        section: '',
        roomType: '1010301',
        roomSubType: '',
        flag: ''
      }
      this.listRoom(DEFAULT_PAGE, DEFAULT_ROW)
    },

    openImportRoomInfo() {
      this.$refs.importOwnerRoom.open()
    },

    openAddRoomFloorModal() {
      this.$refs.addFloor.open()
    },

    async openUpdateRoomFloorModal() {
      if (!this.roomInfo.conditions.floorId) {
        this.$message.warning('请先选择楼栋')
        return
      }

      try {
        const floorData = await this.loadFloor(this.roomInfo.conditions.floorId)
        this.$refs.editFloor.open(floorData)
      } catch (error) {
        console.error('加载楼栋信息失败:', error)
      }
    },

    async openDeleteRoomFloorModal() {
      if (!this.roomInfo.conditions.floorId) {
        this.$message.warning('请先选择楼栋')
        return
      }

      try {
        const floorData = await this.loadFloor(this.roomInfo.conditions.floorId)
        this.$refs.deleteFloor.open(floorData)
      } catch (error) {
        console.error('加载楼栋信息失败:', error)
      }
    },

    openAddRoomUnitModal() {
      if (!this.roomInfo.conditions.floorId) {
        this.$message.warning('请先选择楼栋')
        return
      }
      this.$refs.addUnit.open({ floorId: this.roomInfo.conditions.floorId })
    },

    async openUpdateRoomUnitModal() {
      if (!this.roomInfo.conditions.unitId) {
        this.$message.warning('请先选择单元')
        return
      }

      try {
        const unitData = await this.loadUnits(this.roomInfo.conditions.unitId)
        this.$refs.editUnit.open(unitData)
      } catch (error) {
        console.error('加载单元信息失败:', error)
      }
    },

    async openDeleteRoomUnitModal() {
      if (!this.roomInfo.conditions.unitId) {
        this.$message.warning('请先选择单元')
        return
      }

      try {
        const unitData = await this.loadUnits(this.roomInfo.conditions.unitId)
        this.$refs.deleteUnit.open(unitData)
      } catch (error) {
        console.error('加载单元信息失败:', error)
      }
    },

    changeIsAll() {
      if (this.roomInfo.isAll === 'Y') {
        this.roomInfo.conditions.floorId = ''
        this.roomInfo.conditions.unitId = ''
      }
      this.listRoom(this.roomInfo.currentPage, DEFAULT_ROW)
    },

    toSimplifyAcceptance(room) {
      this.$router.push('/pages/property/simplifyAcceptance?tab=业务受理&searchType=1&searchValue=' + `${room.floorNum}-${room.unitNum}-${room.roomNum}`)
    },

    viewOwnerRooms(room) {
      this.$refs.ownerRooms.open(room)
    },

    viewOwnerMembers(room) {
      this.$refs.ownerMembers.open(room.ownerId)
    },

    viewOwnerCars(room) {
      this.$refs.ownerCars.open(room.ownerId)
    },

    viewComplaints(room) {
      this.$refs.ownerComplaints.open(room.ownerId)
    },

    viewRepairs(room) {
      this.$refs.ownerRepairs.open(room.ownerId)
    },

    viewOweFees(room) {
      this.$refs.ownerOweFees.open(room.ownerId)
    },

    viewRoomOweFees(room) {
      this.$refs.roomOweFees.open(room.roomId)
    },

    viewRoomContracts(room) {
      this.$refs.roomContracts.open({ roomId: room.roomId })
    },

    toRoomBindOwner(room) {
      this.$router.push(`/views/owner/roomBindOwner?roomId=${room.roomId}`)
    },

    toRoomUnBindOwner(room) {
      this.$router.push(`/views/owner/deleteOwnerRoom?ownerId=${room.ownerId}`)
    },

    showMarkdown() {
      this.$router.push('/pages/property/room')
    },

    async loadFloor(floorId) {
      const params = {
        page: 1,
        row: 1,
        communityId: this.getCommunityId(),
        floorId: floorId
      }
      const res = await getFloors(params)
      return res.apiFloorDataVoList[0]
    },

    async loadUnits(unitId) {
      const params = {
        unitId: unitId,
        communityId: this.getCommunityId(),
      }
      const res = await getUnits(params)
      return res[0]
    },
     // 新增：恢复保存的树形组件宽度
     restoreTreeWidth() {
      const savedWidth = localStorage.getItem('roomListTreeWidth')
      if (savedWidth) {
        const width = parseInt(savedWidth)
        if (width >= this.minTreeWidth && width <= this.maxTreeWidth) {
          this.treeWidth = width
        }
      }
    },
    // 新增：保存树形组件宽度
    saveTreeWidth() {
      localStorage.setItem('roomListTreeWidth', this.treeWidth.toString())
    },
    // 新增：开始拖拽调整大小
    startResize(event) {
      event.preventDefault()
      this.isResizing = true
      this.startX = event.clientX
      this.startWidth = this.treeWidth
      
      // 添加拖拽时的样式
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    // 新增：鼠标移动处理
    onMouseMove(event) {
      if (!this.isResizing) return
      
      const deltaX = event.clientX - this.startX
      const newWidth = this.startWidth + deltaX
      
      // 限制宽度范围
      if (newWidth >= this.minTreeWidth && newWidth <= this.maxTreeWidth) {
        this.treeWidth = newWidth
      }
    },
    // 新增：鼠标释放处理
    onMouseUp() {
      if (this.isResizing) {
        this.isResizing = false
        this.saveTreeWidth()
        
        // 恢复鼠标样式
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    },
    // 新增：显示调整大小光标
    showResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = 'col-resize'
      }
    },
    // 新增：隐藏调整大小光标
    hideResizeCursor() {
      if (!this.isResizing) {
        document.body.style.cursor = ''
      }
    },
  }
}
</script>
<style lang="scss" scoped>
.room-container {
    padding: var(--ds-spacing-md);
    height: calc(100vh - 120px);
    overflow: hidden;
    background-color: var(--ds-gray-100);
}

.resizable-layout {
    display: flex;
    height: 100%;
    position: relative;
}

.tree-panel {
    background: #fff;
    border-radius: var(--ds-radius-md);
    padding: var(--ds-spacing-sm);
    overflow-y: auto;
    border: 1px solid var(--ds-gray-300);
    min-height: 450px;
}

.content-panel {
    overflow-y: auto;
}

.resize-handle {
    width: 10px;
    cursor: col-resize;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
}

.resize-handle:hover .resize-line {
    background-color: var(--ds-primary);
}

.resize-line {
    width: 2px;
    height: 30px;
    background-color: var(--ds-gray-300);
    border-radius: 1px;
    transition: background-color var(--ds-transition-fast);
}

.resizable-layout.resizing {
    cursor: col-resize;
}

.resizable-layout.resizing .tree-panel,
.resizable-layout.resizing .content-panel {
    pointer-events: none;
}

.resizable-layout.resizing .resize-handle {
    pointer-events: auto;
}

.margin-top-sm {
    margin-top: var(--ds-spacing-sm);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
    background: #fff;
    border: 1px solid var(--ds-gray-300);
    border-radius: var(--ds-radius-md);
    box-shadow: var(--ds-shadow-sm);
    overflow: hidden;

    &__title {
        font-size: var(--ds-font-size-lg);
        font-weight: 600;
        color: var(--ds-gray-800);
    }
}

/* 表格次要文字 */
.ds-cell-sub {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
    line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
    color: var(--ds-danger) !important;
}

.text-center {
    text-align: center;
}

.tree-panel * {
    user-select: none;
}

.resizable-layout.resizing * {
    user-select: none !important;
}

.resize-handle:hover .resize-line {
    background-color: var(--ds-primary);
}

.resize-line {
    background-color: var(--ds-gray-300);
}
</style>